/* eslint-disable indent */
import { TFunction } from 'i18next';
import { Trans } from 'react-i18next';
import Modal from '@/components/Modal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAddOrUpdateUrlParam,
  useDeleteParamFromUrl,
} from '@/hooks/url-search-params.hooks';
import { ResultActionState } from '@/api/entities/vehicule-check-result';
import {
  ResultModalStep,
  ResultPhotoTab,
} from '@/type/ui/tab/vehiculeCheckResultTab';
import {
  EditGroupIdTab,
  EditGroupTab,
  GroupCheckPointTab,
} from '@/type/ui/tab/groupTab';
import {
  getDamageByClassId,
  getDamageById,
  getDamageByNameAndSeverity,
} from '@/api/damage/damage';
import {
  getEquipmentByClassId,
  getEquipmentById,
} from '@/api/equipment/equipment';
import { Button } from '@/components/Button';
import { Select } from '@/components/Controls/Select';
import { IconInfoCircle } from '@tabler/icons-react';
import { CameraIcon } from '@/components/SvgIcons/CameraIcon';
import { FrameIcon } from '@/components/SvgIcons/FrameIcon';
import { CropIcon } from '@/components/SvgIcons/CropIcon';
import { DownloadIconV2 } from '@/components/SvgIcons/DownloadIconV2';
import { TargetIcon } from '@/components/SvgIcons/TargetIcon';
import { ModerationReportCanvas } from '@/components/Custom/ModerationReportCanvas';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { useGetVehiculeCheckVideo } from '@/hooks/vehicule-check-result.hooks';
import { useGetVehiculeCheckById } from '@/hooks/vehicule-check.hooks';
import { useGetEquipmentsByCheckpointGroup } from '@/hooks/checkpoint-group.hooks';
import { useModerate } from '@/hooks/moderation.hooks';
import {
  editResultImage,
  getResultModalHeading,
  uploadFileToFolder,
} from '@/modules/VehiculeCheckResult/utils/result-image.utils';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { RefObject, useEffect, useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { twMerge } from 'tailwind-merge';
import { useUploadDroppedFile } from '@/utils/upload-drop-file.utils';
import {
  getDamageValueByLabel,
  getEquipmentValueByLabel,
} from '@/utils/ia.utils';
import { MODE_DRAW } from '@/utils/constant.utils';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { ResultVideo } from '@/modules/VehiculeCheckResult/components/ResultVideo';
import Loader from '@/components/Loading/Loader';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';
import {
  getPreparedDamage,
  getPreparedEquipment,
} from '@/modules/VehiculeCheckResult/utils/result-image.utils';
import { PhotoWithMenu } from '@/components/Custom/Photo/PhotoWithMenu';
import { WarningMessage } from '@/components/Message/WarningMessage';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { getLanguage } from '@/utils/language.utils';
import { UUID_REGEX } from '@/utils/regex.utils';
import '../styles.css';

interface ResultImageModalProps {
  t?: TFunction<undefined>;
  vehiculeCheckId?: string;
  checkPointId?: string;
  activeTab?: ResultPhotoTab;
  actionState?: ResultActionState;
  videoRef?: RefObject<HTMLVideoElement>;
  cropConfigs: Crop;
  selectedResultObject?: TResultObjectSchema;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
  onSelectEquipment?: (element: TEquipmentSchema | undefined) => void;
  onSelectDamage?: (element: TDamageSchema | undefined) => void;
  setCropConfigs?: (crop: PixelCrop) => void;
  setCompletedCrop?: (crop: PixelCrop) => void;
  getCroppedImage?: (cropFullVideoByDefault) => Promise<string>;
  mutateUploadImage: (formData: FormData) => void;
  onValidateEditModeration?: () => void;
  canModerate: boolean;
  moderationStatus: string;
  mutateUpdateImage: (vehiculeCheckResult: TVehiculeCheckResultSchema) => void;
}

export const ResultImageModal = ({
  t,
  vehiculeCheckId,
  checkPointId,
  activeTab,
  actionState,
  videoRef,
  cropConfigs,
  selectedResultObject,
  vehiculeCheckResult,
  onSelectEquipment,
  onSelectDamage,
  setCropConfigs,
  setCompletedCrop,
  getCroppedImage,
  mutateUploadImage,
  canModerate,
  moderationStatus,
  mutateUpdateImage,
}: ResultImageModalProps) => {
  const [searchParams] = useSearchParams();
  const { activateModeration, setPolygon, currentItemEdit, polygon } =
    useModerate();
  const step = searchParams.get('step');
  const navigate = useNavigate();
  const { isFrench } = getLanguage();

  const isEdit = actionState === ResultActionState.EDIT;
  const isAdd = actionState === ResultActionState.ADD;

  const containsImage =
    isEdit &&
    (selectedResultObject?.image || selectedResultObject?.customImage);

  const { getNewUrl } = useAddOrUpdateUrlParam();
  const { deleteParam } = useDeleteParamFromUrl();

  // const videoUrl = '/video_result.mp4';
  const { videoUrl, isLoading: isLoadingVideo } =
    useGetVehiculeCheckVideo(vehiculeCheckId);

  const [selectedEquipment, setSelectedEquipment] =
    useState<TEquipmentSchema>();

  const [selectedDamageName, setSelectedDamageName] = useState<string>();

  const [selectedDamage, setSelectedDamage] = useState<TDamageSchema>();

  const [selectedSeverity, setSelectedSeverity] = useState<number>();

  const [videoCurrentTime, setVideoCurrentTime] = useState(0);

  const { data: vehiculeCheck } = useGetVehiculeCheckById(vehiculeCheckId);

  const { equipmentsSelectOptions, isLoading: isLoadingEquipments } =
    useGetModerationEquipments(false);

  const { uniqueDamagesSelectOptions, isLoading: isLoadingDamages } =
    useGetModerationDamages(false);

  const {
    data: equipmentsByCheckpointGroup,
    isLoading: isLoadingEquipmentsByCheckpointGroup,
  } = useGetEquipmentsByCheckpointGroup(
    vehiculeCheck?.data?.groupId,
    checkPointId,
  );

  const emptyEquipmentCheckPoints = !equipmentsByCheckpointGroup?.data?.length;

  const emptyModerateCheckPoints = !canModerate && emptyEquipmentCheckPoints;

  const equipmentIsInList = equipmentsByCheckpointGroup?.data?.find(
    (item) => item?.id === selectedEquipment?.id,
  );

  // const entityIsInList =
  //   activeTab === ResultPhotoTab.EQUIPMENTS
  //     ? Boolean(equipmentIsInList)
  //     : Boolean(damages?.data);

  const {
    uploadedImage: chosenImage,
    errorMessage: chosenImageErrorMessage,
    setUploadedImage,
    chooseFileInputRef,
    handleFileDragOver,
    handleFileDrop,
    handleFileChange,
    handleClickChooseImage,
  } = useUploadDroppedFile({ t });

  useEffect(() => {
    if (chosenImageErrorMessage) {
      alert(chosenImageErrorMessage);
    }
  }, [chosenImageErrorMessage]);

  useEffect(() => {
    (async () => {
      if (selectedResultObject) {
        try {
          // Set selected equipment
          let response = undefined;

          const equipmentId = selectedResultObject?.equipment?.id;

          if (UUID_REGEX.test(equipmentId)) {
            response = await getEquipmentById(equipmentId);
          } else {
            response = await getEquipmentByClassId(Number(equipmentId));
          }

          if (response?.status === 200 && response?.statusText === 'OK') {
            const equipment = getPreparedEquipment(response?.data);

            setSelectedEquipment(equipment);
          } else {
            setSelectedEquipment(undefined);
          }
        } catch (error) {
          console.error('Error fetching Equipment:', error);
          setSelectedEquipment(undefined);
        }

        if (activeTab === ResultPhotoTab.DAMAGES) {
          try {
            // Set selected damage
            let response = undefined;

            const damageId = selectedResultObject?.damage?.id;

            if (UUID_REGEX.test(damageId)) {
              response = await getDamageById(damageId);
            } else {
              response = await getDamageByClassId(Number(damageId));
            }

            if (response?.status === 200 && response?.statusText === 'OK') {
              const damage = getPreparedDamage(response?.data);

              setSelectedDamageName(damage?.name);
              setSelectedSeverity(damage?.severity);

              setSelectedDamage(damage);
            } else {
              setSelectedDamage(undefined);
            }
          } catch (error) {
            console.error('Error fetching Damage:', error);
            setSelectedDamage(undefined);
          }
        }
      }
    })();
  }, [activeTab, selectedResultObject]);

  useEffect(() => {
    (async () => {
      if (selectedDamageName && selectedSeverity) {
        const response = await getDamageByNameAndSeverity(
          selectedDamageName,
          selectedSeverity,
        );
        if (response.status === 200 && response.statusText === 'OK') {
          const damage = getPreparedDamage(response?.data);
          setSelectedDamage(damage);
          onSelectDamage?.(damage);
        } else {
          setSelectedDamage(undefined);
          onSelectDamage?.(undefined);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDamageName, selectedSeverity]);

  const onCloseModal = () => {
    stopVideo();
    deleteParam('step');

    setSelectedEquipment(undefined);
    setSelectedDamage(undefined);
    setSelectedDamageName(undefined);
    setSelectedSeverity(undefined);

    setUploadedImage('');
    setVideoCurrentTime(0);
  };

  const handleSelectChangeEquipment = async (id: string) => {
    let equipment = undefined;

    if (id) {
      const response = await getEquipmentById(id);

      if (response.status === 200 && response.statusText === 'OK') {
        equipment = getPreparedEquipment(response?.data);
      } else {
        equipment = undefined;
      }
    } else {
      equipment = undefined;
    }

    setSelectedEquipment(equipment);
    onSelectEquipment?.(equipment);
  };

  // const handleChangeVehiculeName = (name: string) => {
  //   const entity = {
  //     id: undefined,
  //     name,
  //   };
  //   setSelectedEntity(entity);
  //   onSelectEntity?.(entity);
  // };

  const stopVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleUploadFileToFolder = async (fileUrl: string) => {
    try {
      await uploadFileToFolder({
        fileUrl,
        mutate: mutateUploadImage,
      });
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      onCloseModal();
    }
  };

  const handleCropVideo = async (cropFullVideoByDefault: boolean) => {
    const croppedUrl = await getCroppedImage(cropFullVideoByDefault);
    if (croppedUrl) {
      await handleUploadFileToFolder(croppedUrl);
    }
    stopVideo();
  };

  // const renderEntitySelectOptions = () => {
  //   let isLoading = false;
  //   let data: TEquipmentSchema[] | TDamageSchema[] = [];

  //   switch (true) {
  //     case activeTab === ResultPhotoTab.EQUIPMENTS:
  //       isLoading = isLoadingEquipments;
  //       data = canModerate ? allEquipments?.data : equipments?.data;
  //       break;
  //     case activeTab === ResultPhotoTab.DAMAGES:
  //       isLoading = isLoadingDamages;
  //       data = damages?.data;
  //       break;
  //   }

  //   if (!isLoading && data?.length > 0) {
  //     return data?.map((item) => {
  //       return {
  //         value: item?.id,
  //         label: item?.name,
  //       };
  //     });
  //   }

  //   return undefined;
  // };

  const renderModalContent = () => {
    if (step === ResultModalStep.SELECT_ENTITY) {
      return (
        <>
          {emptyModerateCheckPoints ? (
            <WarningMessage
              containerClassName="max-w-[63%]"
              message={t('configureEquipmentList')}
              clickableText={t('common:byClickingHere').toLowerCase()}
              linkTo={`/group/edit/${vehiculeCheck?.data?.groupId}?tab=${EditGroupTab.REPORT}&childTab=${GroupCheckPointTab.VIDEO_LICENSE_PLATE_NUMBER}#${EditGroupIdTab.EQUIPMENTS_PER_INSPECTION_ID}`}
            />
          ) : selectedEquipment && !canModerate && !equipmentIsInList ? (
            <WarningMessage
              containerClassName="bg-white max-w-[63%] border border-purple-600"
              textClassName="text-purple-600"
              iconClassName="ki-filled ki-outline text-purple-600"
              message={t('thisEquipmentIsNotInYourPreConfiguredList')}
              clickableText={
                isFrench
                  ? t('common:clickHere').toLowerCase()
                  : t('common:byClickingHere').toLowerCase()
              }
              linkTo={`/group/edit/${vehiculeCheck?.data?.groupId}?tab=${EditGroupTab.REPORT}&childTab=${GroupCheckPointTab.VIDEO_LICENSE_PLATE_NUMBER}#${EditGroupIdTab.EQUIPMENTS_PER_INSPECTION_ID}`}
            />
          ) : (
            <></>
          )}
          <div className="flex flex-col items-center mb-6">
            <div className="">
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2.5">
                  {emptyModerateCheckPoints && (
                    <div className="-mt-3.5 flex items-center">
                      <i className="ki-outline ki-information-1 text-red-FF2929 text-2xl" />
                    </div>
                  )}
                  <Select
                    containerClassName="w-full"
                    selectClassName={twMerge(
                      'w-[352px]',
                      emptyModerateCheckPoints &&
                        'border-2 !border-red-FF2929/90',
                    )}
                    placeholder={t('common:chooseAnEquipment')}
                    disabled={!isAdd && !containsImage}
                    options={equipmentsSelectOptions}
                    value={selectedEquipment?.id}
                    onChange={async (e) =>
                      await handleSelectChangeEquipment(e.target.value)
                    }
                  />
                </div>
                {activeTab === ResultPhotoTab.DAMAGES && (
                  <>
                    <Select
                      containerClassName="w-full"
                      placeholder={t('common:chooseADamage')}
                      disabled={!isAdd && !containsImage}
                      options={uniqueDamagesSelectOptions}
                      value={selectedDamageName}
                      onChange={(e) => setSelectedDamageName(e.target.value)}
                    />
                    <Select
                      containerClassName="w-full"
                      placeholder={t('common:chooseASeverity')}
                      disabled={!isAdd && !containsImage}
                      options={[
                        {
                          label: '1',
                          value: 1,
                        },
                        {
                          label: '2',
                          value: 2,
                        },
                        {
                          label: '3',
                          value: 3,
                        },
                      ]}
                      value={selectedSeverity}
                      onChange={(e) =>
                        setSelectedSeverity(Number(e.target.value))
                      }
                    />
                  </>
                )}
              </div>
              {containsImage && (
                <div data-modal-dismiss="true" className="mt-4">
                  <Button
                    buttonClassName="result-modal-button min-w-[352px]"
                    textClassName="mt-0.5 text-2sm"
                    text={
                      activeTab === ResultPhotoTab.DAMAGES
                        ? t('changeOnlyParameters')
                        : t('changeOnlyTheName')
                    }
                    leftIcon={
                      <i className="ki-outline ki-notepad-edit text-lg" />
                    }
                    disabled={!selectedEquipment?.id}
                    onClick={() => {
                      editResultImage({
                        activeImageTab: activeTab,
                        vehiculeCheckResult,
                        selectedEquipment: selectedEquipment,
                        selectedDamage: selectedDamage,
                        checkPointId,
                        selectedResultObjectId: selectedResultObject?.id,
                        canModerate,
                        moderationStatus,
                        mutate: mutateUpdateImage,
                      });
                      setTimeout(() => {
                        onCloseModal();
                      }, 0);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <p className="text-gray-600 text-center text-sm">
            {containsImage &&
            (!emptyModerateCheckPoints ||
              activeTab !== ResultPhotoTab.EQUIPMENTS)
              ? t('orContinueAndSelectOneOfTheOptionsBelow')
              : t('selectOneOfTheOptionsBelow')}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <div data-modal-persistent="true" className="flex flex-1">
              <Button
                text={t('common:captureAnImageFromTheVideo')}
                textClassName="text-sm text-primary"
                leftIcon={
                  <FrameIcon className="w-6 h-6 [&>path]:fill-primary" />
                }
                disabled={
                  !selectedEquipment ||
                  emptyModerateCheckPoints ||
                  (activeTab === ResultPhotoTab.DAMAGES &&
                    !selectedDamage &&
                    !selectedSeverity)
                }
                onClick={() => {
                  const newUrl = getNewUrl({
                    key: 'step',
                    value: ResultModalStep.CAPTURE_VIDEO,
                  });
                  navigate(newUrl);
                }}
                buttonClassName="result-modal-button w-full bg-white"
              />
            </div>
            <div data-modal-persistent="true" className="flex flex-1">
              <Button
                buttonClassName="result-modal-button bg-purple-5433FF"
                text={t('common:useAPreDefinedImage')}
                textClassName="text-sm"
                leftIcon={
                  <CameraIcon className="-mt-1 w-6.5 h-6.5 [&>path]:fill-white" />
                }
                disabled={
                  !selectedEquipment ||
                  emptyModerateCheckPoints ||
                  (activeTab === ResultPhotoTab.DAMAGES &&
                    !selectedDamage &&
                    !selectedSeverity)
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const newUrl = getNewUrl({
                    key: 'step',
                    value: ResultModalStep.UPLOAD_IMAGE,
                  });
                  navigate(newUrl);
                }}
              />
            </div>
          </div>
          {containsImage && (
            <div className="mt-6 pt-5 pb-12 px-10 bg-slate-100/65">
              <div className="flex justify-center items-center gap-1.5 mb-5">
                <i className="ki-filled ki-picture text-purple-600 text-lg -mt-0.5" />
                <div className="text-gray-700 font-medium">
                  {t('common:previewOfTheCurrentImage')}
                </div>
              </div>
              <div className="flex justify-center items-center">
                <PhotoWithMenu
                  containerClassName="hover:cursor-default"
                  imageClassName="w-full h-auto object-cover hover:cursor-default"
                  url={
                    selectedResultObject?.customImage ||
                    selectedResultObject?.image
                  }
                  name=""
                  hideActionButtons={true}
                />
              </div>
            </div>
          )}
        </>
      );
    }

    if (step === ResultModalStep.CAPTURE_VIDEO) {
      return (
        <>
          <p className="text-gray-600 text-center">
            {`${t('positionCursorAtDesiredVideoTime')}.`}
          </p>
          <div className="mt-6 relative flex justify-center bg-black rounded-lg overflow-hidden">
            {videoUrl ? (
              <video
                crossOrigin="anonymous"
                ref={videoRef}
                src={videoUrl}
                className="w-full h-[50vh]"
                controls
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = videoCurrentTime;
                  }
                }}
              />
            ) : (
              <ResultVideo
                containerClassName="h-[50vh]"
                notFoundText={!isLoadingVideo && t('common:noVideoFound')}
              />
            )}
          </div>
          {videoUrl && (
            <div
              data-modal-persistent="true"
              className="mt-6 flex justify-center"
            >
              <Button
                text={t('common:captureTheImage')}
                textClassName="text-sm text-purple-5433FF"
                leftIcon={
                  <FrameIcon className="w-6 h-6 [&>path]:fill-purple-5433FF" />
                }
                onClick={() => {
                  setVideoCurrentTime(videoRef.current?.currentTime);
                  const newUrl = getNewUrl({
                    key: 'step',
                    value: ResultModalStep.CONFIRM_CAPTURE,
                  });
                  navigate(newUrl);
                }}
                buttonClassName="result-modal-button w-full bg-white max-w-[310px]"
              />
            </div>
          )}
        </>
      );
    }

    if (step === ResultModalStep.CONFIRM_CAPTURE) {
      return (
        <>
          <p className="text-gray-600 text-center">
            {`${t('positionCursorAtDesiredVideoTime')}.`}
          </p>
          <div className="mt-6 relative flex justify-center bg-black rounded-lg overflow-hidden">
            <ReactCrop
              crop={cropConfigs}
              onChange={(newCrop) => setCropConfigs(newCrop)}
              onComplete={(c) => setCompletedCrop(c)}
            >
              <video
                crossOrigin="anonymous"
                ref={videoRef}
                src={videoUrl}
                className="w-full h-[50vh]"
                controls={false}
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = videoCurrentTime;
                  }
                }}
              />
            </ReactCrop>
          </div>
          <div className="mt-6 flex justify-center gap-3">
            <div data-modal-dismiss="true" className="flex flex-1">
              <Button
                text={t('common:cropAndValidate')}
                textClassName="text-sm text-purple-5433FF"
                leftIcon={
                  <CropIcon className="-mt-1 w-5 h-5 [&>path]:stroke-purple-5433FF" />
                }
                onClick={async () => await handleCropVideo(false)}
                buttonClassName="result-modal-button w-full bg-white"
              />
            </div>
            <div data-modal-dismiss="true" className="flex flex-1">
              <Button
                text={t('common:validateWithoutCropping')}
                textClassName="text-sm"
                leftIcon={
                  <DownloadIconV2 className="-mt-1 w-5 h-5 [&>path]:fill-white" />
                }
                onClick={async () => await handleCropVideo(true)}
                buttonClassName="result-modal-button bg-purple-5433FF"
              />
            </div>
          </div>
        </>
      );
    }

    if (step === ResultModalStep.UPLOAD_IMAGE) {
      return (
        <>
          <p className="text-gray-600 text-center">{t('dropImageBelow')}</p>
          <p className="mt-2 text-gray-600 text-center text-2sm">
            {t('common:limitSizeFile')}
          </p>
          <div
            className={twMerge(
              `mt-6 flex flex-col justify-center items-center border-2 border-gray-300 border-dashed ${
                activateModeration ? 'h-auto' : 'h-[300px]'
              } rounded-lg`,
              chosenImage && 'border-0 bg-black',
            )}
            onDragOver={handleFileDragOver}
            onDrop={handleFileDrop}
          >
            {chosenImage ? (
              <>
                {activateModeration ? (
                  <ModerationReportCanvas
                    t={t}
                    elementClass={
                      activeTab === ResultPhotoTab.EQUIPMENTS
                        ? getEquipmentValueByLabel(
                            selectedEquipment?.name,
                            equipmentsSelectOptions,
                          )
                        : getDamageValueByLabel(
                            selectedDamage?.name,
                            uniqueDamagesSelectOptions,
                          )
                    }
                    imgSrc={chosenImage}
                    setPolygon={setPolygon}
                    originalBoundingBox={
                      selectedResultObject?.originalBoundingBox
                    }
                    mode={MODE_DRAW.POLYGON}
                  />
                ) : (
                  <img
                    src={chosenImage}
                    alt={t('common:uploadedImage')}
                    className="w-full h-full object-contain rounded-lg"
                  />
                )}
              </>
            ) : (
              <div className="p-8">
                <div className="flex justify-center items-center">
                  <TargetIcon className="w-10 h-10 [&>path]:fill-gray-1E1E1E" />
                </div>
                <div className="mt-3 flex justify-center text-gray-1E1E1E text-xl font-medium">
                  {t('common:dragAndDropImg')}
                </div>
                <div className="mt-3 flex justify-center text-gray-1E1E1E text-sm uppercase">
                  {t('common:or')}
                </div>
                <div
                  className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm hover:cursor-pointer"
                  onClick={handleClickChooseImage}
                >
                  {t('common:searchFolderAndUploadImage')}
                </div>
                <div className="mt-3 text-gray-600 text-2sm text-center">
                  {t('common:sizeMaxMb', { size: 2 })}
                  <br />
                  {t('common:formatNeed')}
                </div>
              </div>
            )}
            <input
              type="file"
              ref={chooseFileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
          {chosenImage ? (
            <div className="mt-6 flex flex-1 justify-center gap-3">
              <div data-modal-persistent="true" className="flex flex-1">
                <Button
                  buttonClassName="result-modal-button bg-white"
                  text={t('common:chooseAnotherImage')}
                  textClassName="text-purple-5433FF"
                  leftIcon={
                    <CameraIcon className="-mt-1 w-6.5 h-6.5 [&>path]:fill-purple-5433FF" />
                  }
                  onClick={handleClickChooseImage}
                />
              </div>
              <div data-modal-dismiss="true" className="flex flex-1">
                <Button
                  text={t('common:validateThisImage')}
                  buttonClassName="result-modal-button"
                  leftIcon={
                    <i className="ki-outline ki-check-circle !text-[22px]" />
                  }
                  onClick={() => handleUploadFileToFolder(chosenImage)}
                />
              </div>
            </div>
          ) : (
            <div
              data-modal-persistent="true"
              className="mt-6 flex flex-1 justify-center"
            >
              <Button
                text={t('common:downloadAnImage')}
                textClassName="text-sm"
                leftIcon={
                  <CameraIcon className="-mt-1 w-6.5 h-6.5 [&>path]:fill-white" />
                }
                onClick={handleClickChooseImage}
                buttonClassName="result-modal-button bg-purple-5433FF max-w-[310px]"
              />
            </div>
          )}
        </>
      );
    }

    if (step === ResultModalStep.MODERATE) {
      return (
        <div className="flex flex-col justify-center gap-4">
          <p className="text-gray-600 text-center">
            {t('redrawYourDetectionForTheElement')}{' '}
            <b>
              {activeTab === ResultPhotoTab.DAMAGES
                ? selectedResultObject?.damage?.name
                : currentItemEdit?.name}
            </b>
          </p>
          <ModerationReportCanvas
            t={t}
            elementClass={
              activeTab === ResultPhotoTab.EQUIPMENTS
                ? getEquipmentValueByLabel(
                    selectedEquipment?.name,
                    equipmentsSelectOptions,
                  )
                : getDamageValueByLabel(
                    selectedDamage?.name,
                    uniqueDamagesSelectOptions,
                  )
            }
            imgSrc={currentItemEdit?.url}
            setPolygon={setPolygon}
            initialCoordinates={currentItemEdit?.coordinatesModerated}
            originalBoundingBox={selectedResultObject?.originalBoundingBox}
            mode={MODE_DRAW.POLYGON}
          />
          <div data-modal-dismiss="true" className="flex flex-1 w-fit m-auto">
            <Button
              text={t('common:validateTheModeration')}
              buttonClassName="result-modal-button"
              leftIcon={
                <i className="ki-outline ki-check-circle !text-[22px]" />
              }
              disabled={!polygon}
              onClick={() => {
                editResultImage({
                  activeImageTab: activeTab,
                  vehiculeCheckResult,
                  selectedEquipment:
                    selectedEquipment || selectedResultObject?.equipment,
                  selectedDamage:
                    selectedDamage || selectedResultObject?.damage,
                  checkPointId,
                  selectedResultObjectId: currentItemEdit?.id,
                  currentImageIndex: currentItemEdit?.['currentImageIndex'],
                  coordinatesModerated: {
                    ...polygon,
                    moderated: true,
                  },
                  canModerate,
                  activateModeration,
                  moderationStatus,
                  mutate: mutateUpdateImage,
                });
                setTimeout(() => {
                  onCloseModal();
                  setPolygon(undefined);
                }, 0);
              }}
            />
          </div>
        </div>
      );
    }

    return <></>;
  };

  return (
    <Modal
      id="result-image-modal"
      title={
        <Trans
          i18nKey={
            getResultModalHeading({
              t,
              step,
              activeTab,
              selectedResultObject,
              isEdit: Boolean(containsImage),
            }).title
          }
          components={{
            span: <span className="mx-1" />,
          }}
        />
      }
      showHeaderBackBtn={
        step &&
        step !== ResultModalStep.SELECT_ENTITY &&
        step !== ResultModalStep.MODERATE
      }
      showCancelBtn={false}
      showSecondBtn={false}
      showFooter={false}
      onClickHeaderBack={() => navigate(-1)}
      onClose={onCloseModal}
      onClickOutsideContent={onCloseModal}
      size="medium"
      containerClassName={twMerge('', containsImage && 'top-[3px]')}
      headerClassName="result-modal-header"
      bodyClassName={twMerge('px-6 pt-0 pb-5 m-0', !containsImage && 'pb-7.5')}
    >
      {renderModalContent()}
      {activateModeration && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <IconInfoCircle className="text-info" stroke={2} />
          <p className="text-center uppercase text-info self-center">
            {t('common:moderationMode')}
          </p>
        </div>
      )}
      {(isLoadingEquipments ||
        isLoadingEquipmentsByCheckpointGroup ||
        isLoadingDamages ||
        isLoadingVideo) && <Loader />}
    </Modal>
  );
};
